import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import EventAccordionPanel from './EventAccordionPanel';
import LoadingSkeleton from '../../atoms/LoadingSkeleton';
import Button from '../../atoms/Button';
import EventFiltersCard from '../EventFiltersCard';

import './EventAccordion.scss';

/**
 * Each event is presented in a collapsible panel, allowing users to expand for more information.
 *
 * Events accept the following keys:
 *
 * * These keys control how and when the event panel is displayed:
 *
 *   category: string. Used for category filtering. Should match one of the category ids given to the 'categories' prop.
 *
 *   hidden: boolean. Controls if this event panel is shown or not. Defaults to false if not provided.
 *
 *   expanded: boolean. Controls if this event panel is expanded or collapsed. Defaults to false if not provided.
 *
 *   onPanelClick: function. Called with no parameters when user clicks to expand or collapse the panel.
 *
 * * These keys control how the event panel appears when collapsed and expanded:
 *
 *   content: JSX. The main content to be displayed in the header.
 *
 *   left: JSX. Additional content displayed on the left side of the header.
 *
 *   right: JSX. Additional content displayed on the right side of the header.
 *
 * * These keys control how the panel appears only when expanded:
 *
 *   details: string. Additional info displayed when the event panel is expanded.
 *
 *   notes: string. User-entered text displayed when the event panel is expanded.
 *
 *   saveNote: function. Called with one string parameter (note) when the user saves a new note.
 *
 *   linkText: string. Text shown as a link at the bottom of the panel.
 *
 *   linkTo: string. An absolute or partial URL that will be passed to the Link component.
 *
 * @component
 */
function EventAccordion({
  className = '',
  events = [],
  scrollToEventId = null,
  loading = false,
  loadingBarnEventFilters = false,
  currentEventFilters = [],
  onResetEventFilters = () => null,
  onEventFiltersChange = () => null,
  rootCauseList = [],
}) {
  // A dict of refs to refer to each panel. Used to enable scrolling.
  const panelRefs = useRef({});
  // The dict is emptied at the start of each render and re-populated as the panels are built.
  panelRefs.current = {};

  // Popover state and ref
  const [popoverVisible, setPopoverVisible] = useState(false);
  const popoverRef = useRef(null);

  useEffect(() => {
    const target = panelRefs.current?.[scrollToEventId];
    if (!target) return;
    target.parentNode.scrollTo({ top: target.offsetTop - target.parentNode.offsetTop, behavior: 'smooth' });
  }, [scrollToEventId]);

  // Handle clicking outside the popover to close it
  useEffect(() => {
    function handleClickOutside(event) {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        setPopoverVisible(false);
      }
    }

    if (popoverVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [popoverVisible]);

  const handlePopoverToggle = () => {
    setPopoverVisible(!popoverVisible);
  };

  const handleEventFilterPopoverClose = () => {
    setPopoverVisible(false);
  };

  const displayedEvents = useMemo(
    () =>
      events.reduce((displayedEvents, { id: eventId, hidden, ...eventProps }) => {
        if (!hidden) {
          /**
           * Callback used to collect a div ref into a dict, keyed by event ID.
           */
          const updatePanelRefs = (divRef) => {
            if (divRef) {
              panelRefs.current[eventId] = divRef;
            }
          };

          displayedEvents.push(
            <div id={eventId} key={eventId} ref={updatePanelRefs} className="EventAccordion-panelContainer">
              <EventAccordionPanel className="EventAccordion-panel" {...eventProps} />
            </div>,
          );
        }
        return displayedEvents;
      }, []),
    [events],
  );

  if (loading) {
    return (
      <div className={`EventAccordion ${className}`}>
        <div className="EventAccordion-header">
          <p className="EventAccordion-headerText">Events</p>
          <LoadingSkeleton className="EventAccordion-dropdown--loading" />
        </div>
        <div className="EventAccordion-panels">
          {Array(6)
            .fill()
            .map((_, index) => (
              <EventAccordionPanel className="EventAccordion-panel" loading key={index} />
            ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`EventAccordion ${className}`}>
      <div className="EventAccordion-header">
        <div className="EventAccordion-headerControls">
          <div className="EventAccordion-visibilityButtonContainer" ref={popoverRef}>
            <Button
              className="EventAccordion-visibilityButton"
              content="Event Filters"
              variant="text"
              left={<img src="/icons/filter.png" alt="Filter" className="EventAccordion-filterIcon" />}
              onClick={handlePopoverToggle}
              title="View fault visibility settings"
            />
            {popoverVisible && (
              <div className="EventAccordion-popover">
                <div className="EventAccordion-popoverContent">
                  <EventFiltersCard
                    loading={loadingBarnEventFilters}
                    currentEventFilters={currentEventFilters}
                    onReset={onResetEventFilters}
                    onClose={handleEventFilterPopoverClose}
                    onEventFiltersChange={onEventFiltersChange}
                    rootCauseList={rootCauseList}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {displayedEvents.length === 0 ? (
        <div className="EventAccordion-emptyEventList">
          <img className="EventAccordion-logo" src="/images/Feedflo-logo.svg" alt="FeedFlo Logo" />
          <p className="EventAccordion-emptyEventListText">No events in the selected time frame</p>
        </div>
      ) : (
        <div className="EventAccordion-panels">{displayedEvents}</div>
      )}
    </div>
  );
}

EventAccordion.propTypes = {
  /**
   * An array of events objects.
   */
  events: PropTypes.arrayOf(
    PropTypes.shape({
      category: PropTypes.string,
      hidden: PropTypes.bool,
      expanded: PropTypes.bool,
      onPanelClick: PropTypes.func,

      content: PropTypes.element,
      left: PropTypes.element,
      right: PropTypes.element,

      details: PropTypes.string,
      notes: PropTypes.string,
      saveNote: PropTypes.func,
      linkText: PropTypes.string,
      linkTo: PropTypes.string,
      rootCause: PropTypes.object,
      rootCauses: PropTypes.array,
      faultCommentId: PropTypes.string,
      updateRootCause: PropTypes.func,
    }),
  ),
  /**
   * An event ID for scrolling the accordion to a specific panel.
   */
  scrollToEventId: PropTypes.string,
  /**
   * Default CSS class override.
   */
  className: PropTypes.string,
  /**
   * Set to show a loading skeleton.
   */
  loading: PropTypes.bool,
  /**
   * List of root causes.
   */
  rootCauseList: PropTypes.array,
  /**
   * Default event filters data.
   */
  currentEventFilters: PropTypes.object,
  /**
   * Loading state for event filters query.
   */
  loadingBarnEventFilters: PropTypes.bool,
  /**
   * Callback function for when event filters are reset to defaults.
   */
  onResetEventFilters: PropTypes.func,
  /**
   * Callback function for when event filters change in real-time.
   */
  onEventFiltersChange: PropTypes.func,
};

export default EventAccordion;
